import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { GridLinkOperator } from "@mui/x-data-grid-premium";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { GridAddIcon } from "@mui/x-data-grid-premium";
import { GridPanelContent } from "@mui/x-data-grid-premium";
import { GridPanelFooter } from "@mui/x-data-grid-premium";
import { GridPanelWrapper } from "@mui/x-data-grid-premium";
import { BryxDataGridFilterPanelForm } from "./BryxDataGridFilterPanelForm";
import { useGridRootProps } from "@mui/x-data-grid-premium";
import { useGridSelector } from "@mui/x-data-grid-premium";
import { gridFilterModelSelector } from "@mui/x-data-grid-premium";
import { gridFilterableColumnDefinitionsSelector } from "@mui/x-data-grid-premium";
import BryxButton from "../BryxButton";
import BryxColors from "../../types/theme";
import { Collapse } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import BryxTypography from "../BryxTypography";
import { forwardRef, useRef, useCallback, useMemo, useEffect } from "react";
const getGridFilter = (col) => ({
    columnField: col.field,
    operatorValue: col.filterOperators[0].value,
    id: Math.round(Math.random() * 1e5),
});
// For one reason or another the majority of this code
// was copied from https://github.com/mui/mui-x/blob/next/packages/grid/x-data-grid/src/components/panel/filterPanel/GridFilterPanel.tsx
export const BryxDataGridFilterPanel = forwardRef(function GridFilterPanel(props, ref) {
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
    const filterableColumns = useGridSelector(apiRef, gridFilterableColumnDefinitionsSelector);
    const lastFilterRef = useRef(null);
    const { linkOperators = [GridLinkOperator.And, GridLinkOperator.Or], columnsSort, filterFormProps, getColumnForNewFilter, children, ...other } = props;
    const applyFilter = useCallback((item) => {
        apiRef.current.upsertFilterItem(item);
    }, [apiRef]);
    const applyFilterLinkOperator = useCallback((operator) => {
        apiRef.current.setFilterLinkOperator(operator);
    }, [apiRef]);
    const getDefaultFilter = useCallback(() => {
        let nextColumnWithOperator;
        if (getColumnForNewFilter && typeof getColumnForNewFilter === "function") {
            // To allow override the column for default (first) filter
            const nextFieldName = getColumnForNewFilter({
                currentFilters: filterModel?.items || [],
                columns: filterableColumns,
            });
            nextColumnWithOperator = filterableColumns.find(({ field }) => field === nextFieldName);
        }
        else {
            nextColumnWithOperator = filterableColumns.find((colDef) => colDef.filterOperators?.length);
        }
        if (!nextColumnWithOperator) {
            return null;
        }
        return getGridFilter(nextColumnWithOperator);
    }, [filterModel?.items, filterableColumns, getColumnForNewFilter]);
    const getNewFilter = useCallback(() => {
        if (getColumnForNewFilter === undefined || typeof getColumnForNewFilter !== "function") {
            return getDefaultFilter();
        }
        const currentFilters = filterModel.items.length ? filterModel.items : [getDefaultFilter()].filter(Boolean);
        // If no items are there in filterModel, we have to pass defaultFilter
        const nextColumnFieldName = getColumnForNewFilter({
            currentFilters: currentFilters,
            columns: filterableColumns,
        });
        const nextColumnWithOperator = filterableColumns.find(({ field }) => field === nextColumnFieldName);
        if (!nextColumnWithOperator) {
            return null;
        }
        return getGridFilter(nextColumnWithOperator);
    }, [filterModel.items, filterableColumns, getColumnForNewFilter, getDefaultFilter]);
    const items = useMemo(() => {
        if (filterModel.items.length) {
            return filterModel.items;
        }
        const defaultFilter = getDefaultFilter();
        return defaultFilter ? [defaultFilter] : [];
    }, [filterModel.items, getDefaultFilter]);
    const hasMultipleFilters = items.length > 1;
    const addNewFilter = () => {
        const newFilter = getNewFilter();
        if (!newFilter) {
            return;
        }
        apiRef.current.upsertFilterItems([...items, newFilter]);
    };
    const deleteFilter = useCallback((item) => {
        const shouldCloseFilterPanel = items.length === 1;
        apiRef.current.deleteFilterItem(item);
        if (shouldCloseFilterPanel) {
            apiRef.current.hideFilterPanel();
        }
    }, [apiRef, items.length]);
    useEffect(() => {
        if (linkOperators.length > 0 && filterModel.linkOperator && !linkOperators.includes(filterModel.linkOperator)) {
            applyFilterLinkOperator(linkOperators[0]);
        }
    }, [linkOperators, applyFilterLinkOperator, filterModel.linkOperator]);
    useEffect(() => {
        if (items.length > 0) {
            lastFilterRef.current.focus();
        }
    }, [items.length]);
    return (_jsxs(GridPanelWrapper, { ref: ref, ...other, sx: { minWidth: "620px", ...other.sx }, children: [_jsxs(GridPanelContent, { sx: { gap: "8px" }, children: [_jsx(BryxTypography, { variant: "h4", sx: { paddingLeft: "8px" }, children: "Create New Filter" }), _jsx(TransitionGroup, { children: items.map((item, index) => (_jsx(Collapse, { children: _jsx(BryxDataGridFilterPanelForm, { item: item, applyFilterChanges: applyFilter, deleteFilter: deleteFilter, hasMultipleFilters: hasMultipleFilters, showMultiFilterOperators: index > 0, multiFilterOperator: filterModel.linkOperator, disableMultiFilterOperator: index !== 1, applyMultiFilterOperatorChanges: applyFilterLinkOperator, focusElementRef: index === items.length - 1 ? lastFilterRef : null, linkOperators: linkOperators, columnsSort: columnsSort, ...filterFormProps }, item.id == null ? index : item.id) }, `${item.id}-${index}`))) })] }), !rootProps.disableMultipleColumnsFiltering && (_jsx(GridPanelFooter, { sx: { padding: "none" }, children: _jsx(BryxButton, { onClick: addNewFilter, startIcon: _jsx(GridAddIcon, {}), variant: "text", sx: {
                        marginLeft: "-16px",
                        fontSize: "13px",
                        color: BryxColors.notify("light", "inactive"),
                        "& svg": {
                            fill: BryxColors.notify("light", "inactive"),
                            height: "16px",
                            width: "16px",
                        },
                    }, disabled: items.length > 0 && items[items.length - 1].columnField == "", children: apiRef.current.getLocaleText("filterPanelAddFilter") }) }))] }));
});
export default BryxDataGridFilterPanel;
